import type { TruckingInvoiceListRow } from '../types/trucking-invoices';
import type { BolBilledOn, RentalFeeOverlap } from '../types/load';

/** Exhaustive over the stored trucking invoice statuses; anything else is a slate dash. */
const STATUS: Record<string, { label: string; fill: string }> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green' },
  paid: { label: 'Paid', fill: 'bg-fi-signal-green' },
  // 0498 (api#1016). A dead row: slate, as Finance → Invoices draws it.
  void: { label: 'Void', fill: 'bg-fi-signal-slate' },
};
export const truckingInvoiceStatusSkin = (s: unknown) =>
  typeof s === 'string' && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : { label: '—', fill: 'bg-fi-signal-slate' };

/**
 * Which invoice bills a BOL, as the API read it from the invoice LINES
 * (api#1011): `billed_on` is the earliest non-void invoice with a line
 * carrying the BOL, `billed_on_count` how many there are. Nothing here works
 * it out from the invoice list: the header's load_id / move_job_id is not the
 * same fact (a BOL linked to a move job after the job was invoiced is on that
 * header and on no line), and reading it called such a BOL covered.
 *
 *   'unknown'  the key is ABSENT: this read does not carry coverage (an older
 *              response, a cached page). Say nothing, never "No invoice".
 *   'none'     `billed_on: null`: the API checked and no invoice bills it.
 *   'billed'   the invoice, and the count (null when the API sent none).
 *
 * `'billed_on' in bol` is the line between the first two. `bol.billed_on ??
 * null` would read an absent key as "none", saying "No invoice" about a BOL
 * the response never answered for.
 */
export type BolCoverage =
  | { kind: 'unknown' }
  | { kind: 'none' }
  | { kind: 'billed'; invoice: BolBilledOn; count: number | null };

export function bolCoverage(bol: unknown): BolCoverage {
  if (!bol || typeof bol !== 'object' || !('billed_on' in bol)) return { kind: 'unknown' };
  const { billed_on: on, billed_on_count: n } = bol as { billed_on: unknown; billed_on_count?: unknown };
  if (on === null) return { kind: 'none' };
  if (!on || typeof on !== 'object' || typeof (on as BolBilledOn).invoice_id !== 'string') return { kind: 'unknown' };
  return { kind: 'billed', invoice: on as BolBilledOn, count: typeof n === 'number' && Number.isFinite(n) ? n : null };
}

/** "Billed on N invoices" when the API counts more than one (double billing), else null. */
export const multipleInvoicesLabel = (c: BolCoverage): string | null =>
  c.kind === 'billed' && c.count != null && c.count > 1 ? `Billed on ${c.count} invoices` : null;

/**
 * The move job's consolidated invoice, if the list holds one. A VOID invoice
 * is skipped: void releases the move job, and the API's own duplicate check on
 * from-move-job skips void the same way (api#1016), so the move job is ready
 * to invoice again rather than "invoiced" by a dead row.
 */
export function invoiceForMoveJob(invoices: TruckingInvoiceListRow[], moveJobId: string): TruckingInvoiceListRow | null {
  return invoices.find((i) => !i.load_id && i.move_job_id === moveJobId && i.status !== 'void') ?? null;
}

/**
 * The one-line warning for a load whose trip a rental mob / demob fee already
 * bills, or `null`. NULL (not checked) and [] (checked, none) both say nothing;
 * a value that is not an array is treated as not checked. A rental dispatch
 * is one direction, so its entries share one fee; mixed entries say both.
 */
export function feeOverlapWarning(overlap: unknown): string | null {
  if (!Array.isArray(overlap) || overlap.length === 0) return null;
  const rows = overlap as RentalFeeOverlap[];
  const fees = new Set(rows.map((r) => r.fee));
  const which = fees.size > 1 ? 'Mob and demob fees already cover' : fees.has('demob_fee') ? 'Demob fee already covers' : 'Mob fee already covers';
  const assets = rows.map((r) => r.asset_number).filter(Boolean);
  return `${which} this trip${assets.length ? ` (${assets.join(', ')})` : ''}.`;
}
